/**
 * MultiItemClaimEditor - the ONE shared line-item editor for the multi-item
 * ("multi-hose") claim flow, used by BOTH Sage (admin) and the consumer.
 *
 * Surface-agnostic by construction: the parent owns the product-search API
 * call (passing `productResults` + `onProductSearch`) and the submit handler,
 * so the same component drives the admin and consumer surfaces. The admin
 * captures the customer; the consumer is session-bound (omit `captureCustomer`).
 *
 * Field rendering reuses the platform's `MetadataField` + `normaliseClaimField`
 * (the same renderer the single-claim wizard uses) so claim-detail fields are
 * always driven by API metadata - never hardcoded. Per item the editor
 * captures product + quantity; the claim details (fault, evidence, ...) are
 * captured once and shared across every item (the common case: N identical
 * items, same fault). Distinct items go in as separate lines.
 */

import { useCallback, useRef, useState } from 'react'
import { ChevronDown, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import {
  ResourceSearchInput,
  type ResourceOption,
} from '@/shared/components/ui/resource-search-input'
import { buildAddress, type Address } from '@/shared/lib/formatting/address'
import {
  MetadataField,
  normaliseClaimField,
  isRequiredValueProvided,
  type FormFieldMeta,
} from '@/shared/lib/forms'
import { DisclaimerAck } from '@/shared/components/claims/disclaimer-ack'
import {
  disclaimerAckValue,
  disclaimerBlocks,
  type DisclaimerConfig,
} from '@/shared/components/claims/disclaimer'
import { cn } from '@/shared/lib/utils'

export interface MultiItemClaimFieldMeta {
  code_name: string
  display_name: string
  field_type: string
  required: boolean
  hint?: string
  configuration_data?: Record<string, unknown>
}

export interface MultiItemClaimSubmitPayload {
  consumer?: {
    email: string
    first_name: string
    last_name: string
    phone: string
    /** Canonical `{display, value}` envelope; omitted when not captured. */
    address?: Address
  }
  lines: {
    product_uuid: string
    quantity: number
    attribute_values: Record<string, unknown>
  }[]
  shared_attribute_values: Record<string, unknown>
}

export interface MultiItemClaimEditorProps {
  title?: string
  description?: string
  /** Capture customer identity (admin). Omit for the consumer (session-bound). */
  captureCustomer?: boolean
  /** Existing consumer matched on the entered email (parent runs the lookup). */
  existingCustomer?: {
    first_name?: string
    last_name?: string
    phone_number?: string
  } | null
  /** Called with the trimmed email so the parent can look up a consumer. */
  onCustomerEmailChange?: (email: string) => void
  /** Child claim capture fields, rendered per item. */
  claimFields?: MultiItemClaimFieldMeta[]
  fieldsLoading?: boolean
  /** Product picker data - the parent owns the search API call. */
  productResults: ResourceOption[]
  onProductSearch: (term: string) => void
  /** True on the picker's first load (no results cached yet). */
  productsLoading?: boolean
  /** True while any product request is in flight (incl. background refetch). */
  productsFetching?: boolean
  /**
   * Address autocomplete provider key (e.g. the consumer `address` attribute's
   * `lookup_provider`). Passed to the `AddressLookupProvider` API; when absent
   * (or no provider is mounted) the address field falls back to manual entry.
   */
  addressLookupProvider?: string
  productPlaceholder?: string
  onSubmit: (payload: MultiItemClaimSubmitPayload) => void
  isSubmitting?: boolean
  submitLabel?: string
  onCancel?: () => void
  error?: string | null
  /** Uploads a file for a per-item capture field and returns the stored ref.
   *  Required for photo/file fields - without it they cannot upload. */
  onFileUpload?: (
    file: File,
    field: FormFieldMeta,
  ) => Promise<{ uuid: string; file_name: string; file_url: string }>
  /**
   * Tenant-authored disclaimer shown once above the submit button and gating
   * submission when acknowledgement is required (DL-44 - "every consumer
   * surface"). Config-driven and optional: callers with no disclaimer omit it
   * and nothing renders. When acknowledged, `disclaimer_acknowledged` is added
   * to `shared_attribute_values` so it is recorded on every child claim.
   */
  disclaimer?: DisclaimerConfig | null
  acknowledged?: boolean
  onAcknowledgedChange?: (value: boolean) => void
}

interface LineDraft {
  key: number
  product: { uuid: string; name: string } | null
  quantity: number
  values: Record<string, unknown>
  expanded: boolean
}

// Mirrors the registration wizard's customer step (StepCustomer): email with
// existing-consumer lookup + prefill, first/last name side by side, and a
// proper PHONE field. Same field types, labels, and hints so the customer
// block reads and renders identically to the single-item register flow.
const CUSTOMER_EMAIL_FIELD: MultiItemClaimFieldMeta = {
  code_name: 'email',
  display_name: 'Email',
  field_type: 'EMAIL',
  required: true,
  hint: 'customer@example.com',
}
const CUSTOMER_FIRST_NAME_FIELD: MultiItemClaimFieldMeta = {
  code_name: 'first_name',
  display_name: 'First Name',
  field_type: 'TEXT',
  required: true,
  hint: 'First name',
}
const CUSTOMER_LAST_NAME_FIELD: MultiItemClaimFieldMeta = {
  code_name: 'last_name',
  display_name: 'Last Name',
  field_type: 'TEXT',
  required: true,
  hint: 'Last name',
}
const CUSTOMER_PHONE_FIELD: MultiItemClaimFieldMeta = {
  code_name: 'phone',
  display_name: 'Phone Number',
  field_type: 'PHONE',
  required: true,
  hint: 'Phone number',
}

export function MultiItemClaimEditor({
  title = 'Multi-item claim',
  description,
  captureCustomer = false,
  existingCustomer,
  onCustomerEmailChange,
  claimFields = [],
  fieldsLoading = false,
  productResults,
  onProductSearch,
  productsLoading = false,
  productsFetching = false,
  addressLookupProvider,
  productPlaceholder = 'Search for a product...',
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Submit claim',
  onCancel,
  error,
  onFileUpload,
  disclaimer = null,
  acknowledged = false,
  onAcknowledgedChange,
}: MultiItemClaimEditorProps) {
  const nextKey = useRef(2)
  const [lines, setLines] = useState<LineDraft[]>([
    { key: 0, product: null, quantity: 1, values: {}, expanded: true },
    { key: 1, product: null, quantity: 1, values: {}, expanded: true },
  ])
  const [customer, setCustomer] = useState<Record<string, unknown>>({})
  const [prefilled, setPrefilled] = useState(false)

  // Consumer mailing address (DL-83). Captured only on the admin surface
  // (`captureCustomer`); the consumer surface is session-bound and already
  // holds the address on the account. Rendered through the shared ADDRESS
  // form renderer (MetadataField) so it matches every other surface; lookup
  // degrades to manual entry when no provider is mounted/configured. The value
  // lives in `customer.address` (camelCase, the AddressInput shape).
  const addressField = normaliseClaimField({
    code_name: 'address',
    display_name: 'Address',
    field_type: 'ADDRESS',
    required: true,
    configuration_data: addressLookupProvider
      ? { lookup_provider: addressLookupProvider }
      : {},
  })

  // The disclaimer acknowledgement supports BOTH controlled (parent passes
  // `acknowledged` + `onAcknowledgedChange`) and uncontrolled (parent passes
  // only `disclaimer`) modes. Without this, a caller that dropped in a
  // `disclaimer` but omitted the optional `onAcknowledgedChange` would get a
  // dead checkbox and a permanently-disabled submit button.
  const [internalAck, setInternalAck] = useState(false)
  const isAcknowledged = onAcknowledgedChange ? acknowledged : internalAck
  const handleAcknowledgedChange = onAcknowledgedChange ?? setInternalAck

  const handlePrefill = () => {
    if (!existingCustomer) return
    setCustomer((prev) => ({
      ...prev,
      first_name: existingCustomer.first_name || prev.first_name,
      last_name: existingCustomer.last_name || prev.last_name,
      phone: existingCustomer.phone_number || prev.phone,
    }))
    setPrefilled(true)
  }

  const addLine = () =>
    setLines((prev) => [
      ...prev,
      {
        key: nextKey.current++,
        product: null,
        quantity: 1,
        values: {},
        expanded: true,
      },
    ])

  const removeLine = (key: number) =>
    setLines((prev) =>
      prev.length > 1 ? prev.filter((l) => l.key !== key) : prev,
    )

  const updateLine = (key: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))

  const updateLineValue = (key: number, code: string, v: unknown) =>
    setLines((prev) =>
      prev.map((l) =>
        l.key === key ? { ...l, values: { ...l.values, [code]: v } } : l,
      ),
    )

  // Per-(line, field) upload-in-progress flags so each file field shows its own
  // spinner. Keyed "<lineKey>:<code>".
  const [uploadingKeys, setUploadingKeys] = useState<Set<string>>(new Set())
  const handleFieldUpload = useCallback(
    async (lineKey: number, file: File, field: FormFieldMeta) => {
      if (!onFileUpload) throw new Error('File upload is not configured.')
      const k = `${lineKey}:${field.code_name}`
      setUploadingKeys((s) => new Set(s).add(k))
      try {
        return await onFileUpload(file, field)
      } finally {
        setUploadingKeys((s) => {
          const n = new Set(s)
          n.delete(k)
          return n
        })
      }
    },
    [onFileUpload],
  )

  // Each item's required child fields must be filled before submit. A required
  // acknowledgement checkbox (BOOLEAN rendered as a checkbox) must be checked -
  // `isRequiredValueProvided` closes the check-then-uncheck hole (DL-44/MH-017).
  const requiredFields = claimFields.filter((f) => f.required)
  const lineComplete = (l: LineDraft) =>
    requiredFields.every((f) => isRequiredValueProvided(f, l.values[f.code_name]))

  const validLines = lines.filter((l) => l.product)
  // Address is stored as the AddressInput camelCase value; `buildAddress`
  // converts to the canonical `{display, value}` envelope and returns null
  // when every component is blank, which doubles as the "provided" check.
  const builtAddress = captureCustomer ? buildAddress(customer.address) : null
  // DL-83: on the admin intake, First/Last/Phone AND Address are all mandatory
  // (email was already required). The consumer surface is session-bound, so
  // `!captureCustomer` short-circuits and none of these gate its submit.
  const customerOk =
    !captureCustomer ||
    (!!String(customer.email ?? '').trim() &&
      !!String(customer.first_name ?? '').trim() &&
      !!String(customer.last_name ?? '').trim() &&
      !!String(customer.phone ?? '').trim() &&
      !!builtAddress)
  const canSubmit =
    validLines.length > 0 &&
    customerOk &&
    validLines.every(lineComplete) &&
    !disclaimerBlocks(disclaimer, isAcknowledged) &&
    !isSubmitting

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit({
      consumer: captureCustomer
        ? {
            email: String(customer.email ?? '').trim(),
            first_name: String(customer.first_name ?? '').trim(),
            last_name: String(customer.last_name ?? '').trim(),
            phone: String(customer.phone ?? '').trim(),
            address: builtAddress ?? undefined,
          }
        : undefined,
      lines: validLines.map((l) => ({
        product_uuid: l.product!.uuid,
        quantity: Math.max(1, Number(l.quantity) || 1),
        attribute_values: l.values,
      })),
      // Record the acknowledgement on every child claim when ticked.
      shared_attribute_values:
        disclaimer && isAcknowledged
          ? { disclaimer_acknowledged: disclaimerAckValue(disclaimer) }
          : {},
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-[760px] flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-[28px] text-[var(--ink)]">{title}</h1>
        {description && (
          <p className="m-0 text-[13px] text-[var(--ink-3)]">{description}</p>
        )}
      </div>

      {error && (
        <div className="rounded-[8px] border-[0.5px] border-[var(--red-border)] bg-[var(--red-bg-subtle)] px-4 py-3 text-[13px] text-[var(--red)]">
          {error}
        </div>
      )}

      {captureCustomer && (
        <section className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
          <p className="mb-4 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
            Customer
          </p>
          <div className="flex flex-col gap-4">
            {/* Email - existing-consumer lookup + prefill hint */}
            <div>
              <MetadataField
                field={normaliseClaimField(CUSTOMER_EMAIL_FIELD)}
                value={customer.email}
                onChange={(v) => {
                  setCustomer((prev) => ({ ...prev, email: v }))
                  setPrefilled(false)
                  onCustomerEmailChange?.(String(v ?? '').trim().toLowerCase())
                }}
                labelVariant="compact"
              />
              {existingCustomer && !prefilled && (
                <p className="mt-1.5 text-[11px] text-[var(--green)]">
                  Existing customer -{' '}
                  <Button
                    variant="link"
                    type="button"
                    onClick={handlePrefill}
                    className="cursor-pointer border-none bg-transparent p-0 text-[11px] font-light text-[var(--green)] underline hover:opacity-80"
                  >
                    pre-fill?
                  </Button>
                </p>
              )}
            </div>

            {/* Name - first and last side by side */}
            <div className="grid grid-cols-2 gap-3">
              <MetadataField
                field={normaliseClaimField(CUSTOMER_FIRST_NAME_FIELD)}
                value={customer.first_name}
                onChange={(v) =>
                  setCustomer((prev) => ({ ...prev, first_name: v }))
                }
                labelVariant="compact"
              />
              <MetadataField
                field={normaliseClaimField(CUSTOMER_LAST_NAME_FIELD)}
                value={customer.last_name}
                onChange={(v) =>
                  setCustomer((prev) => ({ ...prev, last_name: v }))
                }
                labelVariant="compact"
              />
            </div>

            {/* Phone */}
            <MetadataField
              field={normaliseClaimField(CUSTOMER_PHONE_FIELD)}
              value={customer.phone}
              onChange={(v) => setCustomer((prev) => ({ ...prev, phone: v }))}
              labelVariant="compact"
            />

            {/* Address (DL-83) - required. Rendered through the shared ADDRESS
                form renderer so it matches every other surface platform-wide. */}
            <MetadataField
              field={addressField}
              value={customer.address}
              onChange={(v) => setCustomer((prev) => ({ ...prev, address: v }))}
              labelVariant="compact"
            />
          </div>
        </section>
      )}

      <section className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
            Items
          </p>
          <Button type="button" variant="outline" size="xs" onClick={addLine}>
            <Plus className="size-3.5" />
            Add item
          </Button>
        </div>
        <div className="flex flex-col gap-3">
          {lines.map((line, idx) => {
            const showFields = claimFields.length > 0 && !!line.product
            return (
              <div
                key={line.key}
                className="flex flex-col gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-3"
              >
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                      Item {idx + 1}
                    </Label>
                    <ResourceSearchInput
                      value={line.product}
                      onChange={(v) => updateLine(line.key, { product: v })}
                      results={productResults}
                      onSearch={onProductSearch}
                      isLoading={productsLoading}
                      isFetching={productsFetching}
                      iconName="package"
                      searchPlaceholder={productPlaceholder}
                      emptyMessage="No products found"
                    />
                  </div>
                  <div className="w-[88px]">
                    <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                      Qty
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(e) =>
                        updateLine(line.key, {
                          quantity: Math.max(1, Number(e.target.value) || 1),
                        })
                      }
                    />
                  </div>
                  {showFields && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() =>
                        updateLine(line.key, { expanded: !line.expanded })
                      }
                      aria-label={`Toggle details for item ${idx + 1}`}
                    >
                      <ChevronDown
                        className={cn(
                          'size-4 text-[var(--ink-3)] transition-transform',
                          line.expanded && 'rotate-180',
                        )}
                      />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeLine(line.key)}
                    disabled={lines.length <= 1}
                    aria-label={`Remove item ${idx + 1}`}
                  >
                    <Trash2 className="size-4 text-[var(--ink-3)]" />
                  </Button>
                </div>
                {line.product && fieldsLoading && (
                  <p className="border-t-[0.5px] border-t-[var(--rule)] pt-3 text-[13px] text-[var(--ink-3)]">
                    Loading claim details...
                  </p>
                )}
                {showFields && line.expanded && (
                  <div className="grid grid-cols-1 gap-4 border-t-[0.5px] border-t-[var(--rule)] pt-3 sm:grid-cols-2">
                    {claimFields.map((field) => (
                      <MetadataField
                        key={field.code_name}
                        field={normaliseClaimField(field)}
                        value={line.values[field.code_name]}
                        onChange={(v) =>
                          updateLineValue(line.key, field.code_name, v)
                        }
                        onFileUpload={
                          onFileUpload
                            ? (f, fld) => handleFieldUpload(line.key, f, fld)
                            : undefined
                        }
                        uploading={uploadingKeys.has(
                          `${line.key}:${field.code_name}`,
                        )}
                        multiple={!!field.configuration_data?.file_multiple}
                        labelVariant="compact"
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      <Separator />

      {disclaimer && (
        <DisclaimerAck
          disclaimer={disclaimer}
          acknowledged={isAcknowledged}
          onAcknowledgedChange={handleAcknowledgedChange}
        />
      )}

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting ? 'Submitting...' : submitLabel}
        </Button>
      </div>
    </div>
  )
}
